import { CustomerSearchRow } from '../../../api/customersV2';

/**
 * R64 item 7: the committed Kundensuche lives in the URL query (`?q=&type=&status=&note=&tags=&page=&size=`),
 * so „Zurück zur Suche" and browser back restore term, filters and page. The last committed search is also
 * remembered for the browser tab and the signed-in user, so an entry without own parameters (the rail,
 * „Zurück zur Suche") resumes it — this reverses D-U4-20 (Live-Runde 3); emptying the search field, committing
 * the empty search or logging out forgets it. The last result is kept in memory only, to show it instantly
 * while the search re-runs.
 */
export interface CustomerSearchState {
    term: string;
    type: string;
    status: string;
    note: string;
    tags: number[];
    /** 0-based. */
    page: number;
    pageSize: number;
}

export const SEARCH_PAGE_SIZE = 25;

function nonNegative(value: string | null, fallback: number): number {
    const parsed = Number(value);
    return value !== null && value !== '' && Number.isInteger(parsed) && parsed >= 0 ? parsed : fallback;
}

export function parseSearchState(search: string): CustomerSearchState {
    const params = new URLSearchParams(search);
    return {
        term: params.get('q') ?? '',
        type: params.get('type') ?? 'all',
        status: params.get('status') ?? 'all',
        note: params.get('note') ?? '',
        tags: (params.get('tags') ?? '')
            .split(',')
            .filter(entry => entry !== '')
            .map(Number)
            .filter(Number.isInteger),
        page: Math.max(nonNegative(params.get('page'), 1) - 1, 0),
        pageSize: nonNegative(params.get('size'), SEARCH_PAGE_SIZE),
    };
}

/** Canonical query string ('' for the empty search) — the URL and the result memory share it. */
export function searchStateQuery(state: CustomerSearchState): string {
    const params = new URLSearchParams();
    if (state.term) params.set('q', state.term);
    if (state.type !== 'all') params.set('type', state.type);
    if (state.status !== 'all') params.set('status', state.status);
    if (state.note) params.set('note', state.note);
    if (state.tags.length > 0) params.set('tags', state.tags.join(','));
    if (state.page > 0) params.set('page', String(state.page + 1));
    if (state.pageSize !== SEARCH_PAGE_SIZE) params.set('size', String(state.pageSize));
    const query = params.toString();
    return query ? `?${query}` : '';
}

const STATE_KEYS = ['q', 'type', 'status', 'note', 'tags', 'page', 'size'];

/** This panel's part of a URL query, canonical — foreign parameters are ignored. */
export const ownSearchQuery = (search: string): string => searchStateQuery(parseSearchState(search));

/** The URL query with this panel's keys replaced by `query`; foreign parameters stay. */
export function withSearchQuery(search: string, query: string): string {
    const params = new URLSearchParams(search);
    STATE_KEYS.forEach(key => params.delete(key));
    new URLSearchParams(query).forEach((value, key) => params.set(key, value));
    const result = params.toString();
    return result ? `?${result}` : '';
}

interface RememberedQuery {
    /** OIDC subject of the user who committed the search. */
    sub: string;
    query: string;
}

interface SearchMemory {
    query: RememberedQuery | null;
    result: { query: string; items: CustomerSearchRow[]; total: number } | null;
}

const QUERY_STORAGE_KEY = 'kd.lastSearch';

const memory: SearchMemory = { query: null, result: null };

const storeQuery = (remembered: RememberedQuery | null): void => {
    memory.query = remembered;
    try {
        window.sessionStorage.removeItem(QUERY_STORAGE_KEY);
        if (remembered) window.sessionStorage.setItem(QUERY_STORAGE_KEY, JSON.stringify(remembered));
    } catch {
        // blocked or full storage: the module memory serves this page load, the removal leaves no older search
    }
};

export const rememberSearchQuery = (query: string, sub: string): void => storeQuery(query ? { sub, query } : null);

function parseRememberedQuery(stored: string): RememberedQuery | null {
    try {
        const parsed = JSON.parse(stored);
        return typeof parsed?.sub === 'string' && typeof parsed.query === 'string' ? parsed : null;
    } catch {
        // the plain query string of the previous build has no owner
        return null;
    }
}

/** Live-Runde 3: only the user who committed the search gets it back; any other remembered search is dropped. */
function rememberedSearchQuery(sub: string): string {
    let stored: string | null = null;
    try {
        stored = window.sessionStorage.getItem(QUERY_STORAGE_KEY);
    } catch {
        // blocked storage: the module memory serves this page load
    }
    const remembered = stored === null ? memory.query : parseRememberedQuery(stored);
    if (remembered?.sub === sub) return remembered.query;
    storeQuery(null);
    return '';
}

/** The URL's own search, else (a bare entry: the rail, „Zurück zur Suche") the remembered one of `sub`; '' = none. */
export const requestedSearchQuery = (search: string, sub: string): string =>
    ownSearchQuery(search) || rememberedSearchQuery(sub);

export const rememberSearchResult = (query: string, items: CustomerSearchRow[], total: number): void => {
    memory.result = { query, items, total };
};

export const rememberedSearchResult = (): SearchMemory['result'] => memory.result;

export const clearSearchMemory = (): void => {
    storeQuery(null);
    memory.result = null;
};
